import { Badge } from "src/components/ui/badge";
import { Button } from "src/components/ui/button";
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from "src/components/ui/drawer";
import { ScrollBar } from "../../../components/ui/scroll-area";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "../../../components/ui/table";
import { ScrollArea } from "../../../components/ui/scroll-area";
import { useProductsAEB } from "../../helper/ProductContextAEB";
import type { Brand } from "../../helper/ProductContextAEB";
import { Pencil2Icon, UploadIcon } from "@radix-ui/react-icons";
import { useState } from "react";

// shadcn/ui Form Controls
import { Label } from "src/components/ui/label";
import { Input } from "src/components/ui/input";
import { Textarea } from "src/components/ui/textarea";
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from "src/components/ui/select";

type ManufacturerRole = "MANUFACTURER" | "DISTRIBUTOR";

interface ManufacturerFormState {
  name: string;
  address: string;
  email: string;
  roles: ManufacturerRole;
  weeeNumber: string;
  batteryRegistrationNumber: string;
  additionalInfo: string;
}

const BrandManufacturerDrawer: React.FC<{ brand: Brand }> = ({ brand }) => {
  const { getManufacturerDetails, updateManufacturerDetails } = useProductsAEB();

  const [formState, setFormState] = useState<ManufacturerFormState>({
    name: "",
    address: "",
    email: "",
    roles: "MANUFACTURER",
    weeeNumber: "",
    batteryRegistrationNumber: "",
    additionalInfo: "",
  });

  const [webshopPreview, setWebshopPreview] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const loadDetails = async () => {
    setIsLoading(true);
    setError(null);

    const data = await getManufacturerDetails(brand.id);

    if (data) {
      const { editableFields, webshopPreview } = data;
      const rolesRaw = editableFields.roles;

      let role: ManufacturerRole = "MANUFACTURER";
      if (Array.isArray(rolesRaw) && rolesRaw.length > 0) {
        role = (rolesRaw[0] as ManufacturerRole) || "MANUFACTURER";
      } else if (typeof rolesRaw === "string" && rolesRaw.length > 0) {
        role = (rolesRaw as ManufacturerRole) || "MANUFACTURER";
      }

      setFormState({
        name: editableFields.brandName || brand.name,
        address: editableFields.address || "",
        email: editableFields.email || "",
        roles: role,
        weeeNumber: editableFields.weeeNumber || "",
        batteryRegistrationNumber: editableFields.batteryRegistrationNumber || "",
        additionalInfo: editableFields.additionalInfo || "",
      });
      setWebshopPreview(webshopPreview || "");
    } else {
      setError("Herstellerdetails konnten nicht geladen werden.");
    }

    setIsLoading(false);
  };

  const handleChange = (field: keyof ManufacturerFormState, value: string) => {
    setFormState((prev) => ({
      ...prev,
      [field]: field === "roles" ? (value as ManufacturerRole) : value,
    }));
  };

  const handleSave = async () => {
    setIsSaving(true);
    setError(null);

    try {
      await updateManufacturerDetails(brand.id, {
        name: formState.name,
        address: formState.address,
        email: formState.email,
        roles: formState.roles,
        weeeNumber: formState.weeeNumber,
        batteryRegistrationNumber: formState.batteryRegistrationNumber,
        additionalInfo: formState.additionalInfo,
      });

      const refreshed = await getManufacturerDetails(brand.id);
      if (refreshed) {
        setWebshopPreview(refreshed.webshopPreview || "");
      }
    } catch (e) {
      setError("Beim Speichern ist ein Fehler aufgetreten.");
    } finally {
      setIsSaving(false);
    }
  };

  const initialLetter = brand.name?.[0]?.toUpperCase() ?? "?";

  return (
    <Drawer>
      <DrawerTrigger asChild>
        <Button variant="outline" size="icon" onClick={loadDetails}>
          <Pencil2Icon className="w-5 h-5" />
        </Button>
      </DrawerTrigger>

      <DrawerContent className="h-[95vh] flex flex-col px-4 sm:px-8 lg:px-12 z-[90]">
        <div className="mx-auto flex w-full max-w-5xl flex-1 flex-col min-h-0">
          <DrawerHeader className="flex flex-col gap-4 pb-2">
            <div className="flex items-center gap-3">
              <div className="flex h-9 w-9 items-center justify-center rounded-full bg-primary/10 text-sm font-semibold text-primary">
                {initialLetter}
              </div>
              <div className="flex flex-col">
                <DrawerTitle className="text-xl sm:text-2xl md:text-3xl">Kontaktdaten des Herstellers</DrawerTitle>
                <p className="text-xs text-muted-foreground">
                  Marke: <span className="font-medium">{brand.name}</span>
                </p>
              </div>
              <div className="ml-auto">
                <Badge variant="outline" className="text-[10px] uppercase tracking-wide">
                  {formState.roles}
                </Badge>
              </div>
            </div>
            <DrawerDescription className="mt-0 text-sm text-muted-foreground">
              Verwalten Sie hier die Herstellerdaten, die auch für den Webshop genutzt werden.
            </DrawerDescription>
          </DrawerHeader>

          <ScrollArea className="flex-1 min-h-0">
            <ScrollBar orientation="vertical" />
            <section className="grid grid-cols-1 gap-6 p-2 pb-8 md:grid-cols-4 md:p-4">
              {/* Linke Spalte: Brand & Webshop-Preview */}
              <div className="md:col-span-1 space-y-4">
                <div className="rounded-xl border bg-card/60 p-4 shadow-sm">
                  <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground mb-2">
                    Webshop-Vorschau
                  </p>
                  <p className="text-sm leading-relaxed whitespace-pre-line">
                    {isLoading
                      ? "Herstellerdaten werden geladen ..."
                      : webshopPreview
                      ? webshopPreview
                      : "Für diese Marke sind noch keine Herstellerdaten im Webshop hinterlegt."}
                  </p>
                </div>
                <div className="rounded-xl border bg-muted/60 p-3 text-xs text-muted-foreground space-y-1">
                  <p className="font-medium text-[11px] tracking-wide uppercase">Hinweis</p>
                  <p>Name und Adresse sind Pflichtfelder und werden im Webshop prominent bei der Marke angezeigt.</p>
                </div>
              </div>

              {/* Rechte Spalte: Formular */}
              <div className="md:col-span-3">
                {error && (
                  <div className="mb-4 inline-flex w-full items-center gap-2 rounded-lg border border-destructive/60 bg-destructive/5 px-3 py-2 text-xs text-destructive">
                    {error}
                  </div>
                )}

                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <div className="md:col-span-2 space-y-2">
                    <Label htmlFor={`manufacturer-name-${brand.id}`}>Herstellername</Label>
                    <Input
                      id={`manufacturer-name-${brand.id}`}
                      type="text"
                      placeholder="z. B. Nachhaltige Produktion im Fokus"
                      value={formState.name}
                      disabled={isLoading}
                      onChange={(e) => handleChange("name", e.target.value)}
                    />
                  </div>

                  <div className="md:col-span-2 space-y-2">
                    <Label htmlFor={`manufacturer-address-${brand.id}`}>Adresse</Label>
                    <Textarea
                      id={`manufacturer-address-${brand.id}`}
                      placeholder="z. B. Musterstraße 1, 12345 Reichenbach"
                      value={formState.address}
                      disabled={isLoading}
                      className="min-h-[6rem]"
                      onChange={(e) => handleChange("address", e.target.value)}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor={`manufacturer-email-${brand.id}`}>E-Mail</Label>
                    <Input
                      id={`manufacturer-email-${brand.id}`}
                      type="email"
                      placeholder="kontakt@hersteller.de"
                      value={formState.email}
                      disabled={isLoading}
                      onChange={(e) => handleChange("email", e.target.value)}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor={`manufacturer-role-${brand.id}`}>Rolle</Label>
                    <Select
                      value={formState.roles}
                      onValueChange={(value) => handleChange("roles", value)}
                      disabled={isLoading}>
                      <SelectTrigger id={`manufacturer-role-${brand.id}`}>
                        <SelectValue placeholder="Rolle auswählen" />
                      </SelectTrigger>
                      <SelectContent className="z-[99]">
                        <SelectItem value="MANUFACTURER">MANUFACTURER</SelectItem>
                        <SelectItem value="DISTRIBUTOR">DISTRIBUTOR</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor={`manufacturer-weee-${brand.id}`}>WEEE-Reg.-Nr.</Label>
                    <Input
                      id={`manufacturer-weee-${brand.id}`}
                      type="text"
                      placeholder="z. B. DE 12345678"
                      value={formState.weeeNumber}
                      disabled={isLoading}
                      onChange={(e) => handleChange("weeeNumber", e.target.value)}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor={`manufacturer-battery-${brand.id}`}>Batterie-Reg.-Nr.</Label>
                    <Input
                      id={`manufacturer-battery-${brand.id}`}
                      type="text"
                      placeholder="z. B. 21001234"
                      value={formState.batteryRegistrationNumber}
                      disabled={isLoading}
                      onChange={(e) => handleChange("batteryRegistrationNumber", e.target.value)}
                    />
                  </div>

                  <div className="md:col-span-2 space-y-2">
                    <Label htmlFor={`manufacturer-additional-${brand.id}`}>Zusätzliche Informationen</Label>
                    <Textarea
                      id={`manufacturer-additional-${brand.id}`}
                      placeholder="Freifeld für weitere Compliance-Hinweise, Zertifikate, Warnhinweise ..."
                      value={formState.additionalInfo}
                      disabled={isLoading}
                      className="min-h-[5rem]"
                      onChange={(e) => handleChange("additionalInfo", e.target.value)}
                    />
                  </div>
                </div>
              </div>
            </section>
          </ScrollArea>

          <DrawerFooter className="flex flex-row items-center justify-between gap-4 border-t pt-4">
            <p className="text-xs text-muted-foreground">
              Änderungen werden direkt nach dem Speichern im Webshop wirksam.
            </p>
            <div className="flex items-center gap-3">
              <Button variant="default" className="flex gap-2" disabled={isSaving || isLoading} onClick={handleSave}>
                <UploadIcon className="w-4 h-4" />
                {isSaving ? "Speichere ..." : "Informationen speichern"}
              </Button>
              <DrawerClose asChild>
                <Button variant="outline">Schließen</Button>
              </DrawerClose>
            </div>
          </DrawerFooter>
        </div>
      </DrawerContent>
    </Drawer>
  );
};

const ProductManufacturer: React.FC = () => {
  const { allBrands } = useProductsAEB();

  return (
    <section className="w-full h-full p-4 sm:p-6 lg:p-8 space-y-4">
      <div className="flex flex-col gap-2">
        <h1 className="text-xl sm:text-2xl font-semibold tracking-tight">Herstellerdaten pro Marke</h1>
        <p className="text-sm text-muted-foreground max-w-2xl">
          Pflegen Sie hier die Kontaktdaten der Hersteller. Diese Informationen werden im Webshop bei der jeweiligen
          Marke angezeigt.
        </p>
      </div>

      <ScrollArea className="w-full rounded-xl border bg-card/60">
        <ScrollBar orientation="horizontal" />
        <Table className="min-w-full text-sm">
          <TableHeader>
            <TableRow>
              <TableHead className="w-full">Marke</TableHead>
              <TableHead className="w-[140px] text-center">Aktionen</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {Array.isArray(allBrands) && allBrands.length > 0 ? (
              allBrands.map((brand) => (
                <TableRow key={brand.id} className="dark:text-white">
                  <TableCell className="py-3">
                    <div className="flex items-center gap-3">
                      <div className="flex h-7 w-7 items-center justify-center rounded-full bg-muted text-xs font-semibold">
                        {brand.name?.[0]?.toUpperCase() ?? "?"}
                      </div>
                      <span className="font-medium">{brand.name}</span>
                    </div>
                  </TableCell>
                  <TableCell className="py-3">
                    <div className="flex items-center justify-center">
                      <BrandManufacturerDrawer brand={brand} />
                    </div>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={8} className="text-center py-12 text-sm text-muted-foreground">
                  Es wurden keine Marken gefunden.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        <ScrollBar orientation="horizontal" />
      </ScrollArea>
    </section>
  );
};

export default ProductManufacturer;
